import { useEffect, useMemo, useState } from "react";
import config from "../config/index.js";
import ZoneIndicator, { keeperAction } from "../components/ZoneIndicator.jsx";
import { useTvtZone } from "../hooks/useTvtZone.js";
import { useProtocolHealth } from "../hooks/useProtocolHealth.js";
import { useTreasury } from "../hooks/useTreasury.js";
import { useTvt } from "../hooks/useTvt.js";
import { fetchInterestCoverage, fetchRecentTransactions, fetchLendingHealth, fetchTvtSeries } from "../services/api.js";
import { chainNowMs } from "../utils/chainClock.js";

/**
 * Protocol Health — public transparency page.
 *
 * Sourced LIVE from the backend (which in turn reads the on-chain
 * contracts). The layout/styling is the editorial spec the client
 * supplied; the numbers are real. Loading states render an em-dash so
 * the page never silently displays a stale or fabricated figure.
 */
export default function ProtocolHealth() {
    const protocolHealth = useProtocolHealth({ pollMs: 60_000 });
    const treasury = useTreasury({ pollMs: 60_000 });
    const tvt = useTvt({ pollMs: 60_000 });
    const [interestCoverage, setInterestCoverage] = useState(null);
    const [recentActivity, setRecentActivity] = useState(null);
    const [lendingHealth, setLendingHealth] = useState(null);
    // 30-day TVG series (downsampled) for the ratchet chart — the raw last-90
    // snapshots only cover a few hours and read flat; the windowed series shows
    // the real multi-day movement in the treasury-backed floor.
    const [tvgSeries, setTvgSeries] = useState(null);
    // Live §F zone (executor's own spot + intrinsic) — used to show WHICH action
    // the treasury is running (Skimming / Harvesting / Buying / Holding), which
    // the zone pill alone didn't explain.
    const { data: zoneData } = useTvtZone();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchInterestCoverage();
                if (!cancelled) setInterestCoverage(data);
            } catch {
                /* leave as null — UI shows em-dash */
            }
            try {
                const data = await fetchTvtSeries({ windowId: "30d", samples: 90 });
                if (!cancelled) setTvgSeries(data?.samples || null);
            } catch {
                /* leave as null — ratchet chart falls back to tvt.history */
            }
            try {
                // Backend caps `limit` at 200 (transaction.routes.js). Requesting
                // 250 returned a 400 → the fetch threw → recentActivity stayed
                // null → the "new deposits / early exits" windows all read 0.
                const data = await fetchRecentTransactions({ limit: 200 });
                if (!cancelled) setRecentActivity(data?.items || []);
            } catch {
                /* leave as null */
            }
            try {
                const data = await fetchLendingHealth();
                if (!cancelled) setLendingHealth(data);
            } catch {
                /* leave as null */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const aggregate = useMemo(
        () => computeAggregates({
            protocolHealth: protocolHealth.data,
            treasury: treasury.data,
            tvt,
            interestCoverage,
            recentActivity,
            lendingHealth,
            tvgSeries,
            zoneData
        }),
        [protocolHealth.data, treasury.data, tvt, interestCoverage, recentActivity, lendingHealth, tvgSeries, zoneData]
    );

    return (
        <div className="w-full">
            <ProtocolHealthHero aggregate={aggregate} />
            <TrustLayer aggregate={aggregate} />
            {/* §M6 (Jul 2026 client review): "'View Protocol Health' doesn't
                really do anything here, maybe remove or move it to the top of
                the page." Moved — the jump-to-detail CTA now lives in the hero
                (see ProtocolHealthHero), and the mid-page bar is gone. */}
            <FullProtocolHealth aggregate={aggregate} />
            <OnChainCTA />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Aggregation                                                                */
/* -------------------------------------------------------------------------- */

function computeAggregates({
    protocolHealth,
    treasury,
    tvt,
    interestCoverage,
    recentActivity,
    lendingHealth,
    tvgSeries,
    zoneData
}) {
    const ONE_USDC = 1_000_000n;

    // §F action the treasury is running right now, from the executor's own
    // on-chain spot + intrinsic (the same pair it classifies zones with).
    const zSpot = Number(zoneData?.spotPrice || 0);
    const zIntrinsic = Number(zoneData?.intrinsicValuePerGil || 0);
    const zoneRatio = zSpot > 0 && zIntrinsic > 0 ? zSpot / zIntrinsic : null;
    const zoneAction = keeperAction(zoneRatio);

    // Segregated principal held in the Safe Vault (the 80% slice of every
    // active deposit) — the "Principal segregated and untouched" figure.
    // The API exposes it as `accountedPrincipal`; the old `balance` field
    // never existed, so this tile always read $0.
    const safeVaultBalance = bigOr(
        protocolHealth?.safeVault?.accountedPrincipal,
        protocolHealth?.safeVault?.balance
    );
    const totalActivePrincipal = bigOr(protocolHealth?.deposits?.totalActivePrincipal);
    const interestReserve = bigOr(protocolHealth?.treasury?.interestReserve, treasury?.interestReserve);
    const buybackReserve = bigOr(protocolHealth?.treasury?.buybackReserve, treasury?.buybackReserve);
    const operationalReserve = bigOr(protocolHealth?.treasury?.operationalReserve, treasury?.operationalReserve);
    const treasuryAllocated = bigOr(treasury?.totalAllocated);
    // §M6 — the amount currently lent back out of the Safe Vault.
    //
    // This read `protocolHealth.totalLoaned`, which does not exist: the health
    // payload nests it as `safeVault.totalLoaned`. The Treasury fallback is
    // also always 0 now, because loans are funded from the Safe Vault, not the
    // Treasury. So the figure resolved to zero and the Safe Vault card claimed
    // "Nothing is currently advanced out" while $15,448 was advanced out —
    // exactly the overstatement the client flagged.
    const outstandingLoanPrincipal = bigOr(
        protocolHealth?.safeVault?.totalLoaned,
        treasury?.outstandingLoanPrincipal
    );
    // USDC physically sitting in the vault right now. `accountedPrincipal` is
    // the depositor's CLAIM and deliberately does not shrink when a loan is
    // drawn (SafeVault.disburseLoan leaves `_principalByDeposit` untouched), so
    // the claim alone is always exactly 80% of TVL and can never answer "how
    // much is actually in there".
    const safeVaultCash = safeVaultBalance > outstandingLoanPrincipal
        ? safeVaultBalance - outstandingLoanPrincipal
        : 0n;

    const totalReserves = interestReserve + buybackReserve + operationalReserve;

    // MEASURED share of TVL actually held as USDC in the Safe Vault. Not the
    // 80% routing constant, and not the claim either — loan draws (Leverage +
    // the Turbo loop) move real USDC out, so this is always ≤ 80% and on a
    // Turbo-heavy book it is far below it.
    const safeVaultShareOfTvlPct = totalActivePrincipal > 0n
        ? Number((safeVaultCash * 1000n) / totalActivePrincipal) / 10
        : null;

    // Forward coverage in DAYS.
    //
    // §M6 (Jul 2026 client review): "How is this number being calculated? It
    // should be Yield Reserve + Treasury." It previously divided only the
    // Yield (interest) Reserve by the daily coupon bill, which understated
    // the runway — the Buyback and Operational buckets are also USDC sitting
    // in the Distribution Reserve and are reachable for coupons before the
    // protocol would ever default. Numerator is now the WHOLE Distribution
    // Reserve (`totalReserves` = interest + buyback + operational), which is
    // what the client means by "Yield Reserve + Treasury".
    //
    // Denominator is unchanged: the daily interest bill across all active
    // principal at the contract's 20% simple APR.
    const dailyInterestObligation =
        (totalActivePrincipal * BigInt(config.protocol.annualRateBps || 2000)) / (10_000n * 365n);
    const coverageDays = dailyInterestObligation > 0n
        ? Number(totalReserves / dailyInterestObligation)
        : null;
    // Yield-Reserve-only runway, kept alongside so the detail table can show
    // both figures and the headline card's split is auditable.
    const coverageDaysYieldReserveOnly = dailyInterestObligation > 0n
        ? Number(interestReserve / dailyInterestObligation)
        : null;

    const tvtCurrent = tvt?.current?.tvt || null;
    const tvtValid = Boolean(tvt?.current?.tvtValid);
    // Ratcheted TVG floor in plain USDC. The on-chain `tvt` rounds to 0 while
    // the treasury is tiny, so we derive the intrinsic backing (treasury ÷
    // circulating supply) across every snapshot + the live figure and take the
    // RUNNING MAXIMUM — the floor "only moves up, never down", so a later dip in
    // raw backing must not lower the reported floor. Shown instead of a "—".
    const tvtCurrentFloorUsdc = (() => {
        const samples = [...(tvt?.history || []), tvt?.current]
            .filter(Boolean)
            .map(intrinsicUsdcPerGil)
            .filter((v) => v > 0);
        return samples.length ? Math.max(...samples) : null;
    })();
    // Live GIL market price (USDC-per-GIL × 1e8) — shown next to the TVG floor.
    const gilSpotPrice = tvt?.current?.spotPrice ?? null;
    const tvtHistory = tvt?.history || [];
    const tvtSpark = buildSparkline(tvtHistory.map((p) => Number(p.tvt)));
    const ratchetEvents = countRatchetEvents(tvtHistory);
    const ratchetDelta30d = ratchetDelta(tvtHistory, 30);
    const allTimeLow = tvtHistory.length
        ? tvtHistory.reduce((acc, p) => (acc === null || p.tvt < acc ? p.tvt : acc), null)
        : null;

    // Chain time: the 24h/7d/30d activity windows compare against chain
    // event timestamps, so "now" must be the block clock, not wall time.
    const now = chainNowMs();
    const oneDay = 24 * 60 * 60 * 1000;
    const within = (millis, txAt) => now - new Date(txAt).getTime() <= millis;
    const items = recentActivity || [];
    const deposits24h = items.filter((x) => x.type === "deposit_opened" && within(oneDay, x.createdAt || x.timestamp)).length;
    const deposits7d = items.filter((x) => x.type === "deposit_opened" && within(7 * oneDay, x.createdAt || x.timestamp)).length;
    const deposits30d = items.filter((x) => x.type === "deposit_opened" && within(30 * oneDay, x.createdAt || x.timestamp)).length;
    const exits30d = items.filter((x) => x.type === "early_exit" && within(30 * oneDay, x.createdAt || x.timestamp)).length;
    const totalYieldPaid30d = items
        .filter((x) => (x.type === "deposit_settled" || x.type === "auto_compound") && within(30 * oneDay, x.createdAt || x.timestamp))
        .reduce((acc, x) => acc + Number(BigInt(x.amount || x.value || "0") / ONE_USDC), 0);

    return {
        ONE_USDC,
        safeVaultBalance,
        totalActivePrincipal,
        interestReserve,
        buybackReserve,
        operationalReserve,
        treasuryAllocated,
        outstandingLoanPrincipal,
        totalReserves,
        activeDepositCount: protocolHealth?.deposits?.activeCount ?? null,
        totalDepositCount: protocolHealth?.deposits?.totalCount ?? null,
        totalWallets: protocolHealth?.deposits?.totalWallets ?? null,
        coverageDays,
        coverageDaysYieldReserveOnly,
        safeVaultShareOfTvlPct,
        safeVaultCash,
        // The /api/solvency/interest-coverage endpoint returns `coverageBps`
        // (not `coverageRatioBps`), with `interestReserve`/`interestObligation`
        // as the actual/required figures. Reading the old names left the
        // "Coverage ratio" row blank. Fall back to solvency.coverageBps.
        coverageRatioBps:
            interestCoverage?.coverageBps ?? protocolHealth?.solvency?.coverageBps ?? null,
        coverageRequired: interestCoverage?.interestObligation
            ? BigInt(interestCoverage.interestObligation)
            : null,
        coverageActual: interestCoverage?.interestReserve
            ? BigInt(interestCoverage.interestReserve)
            : null,
        marketingLocked: Boolean(interestCoverage?.marketingLocked ?? protocolHealth?.marketingLocked),
        tvtCurrent,
        tvtCurrentFloorUsdc,
        tvtValid,
        gilSpotPrice,
        tvtSpark,
        ratchetEvents,
        ratchetDelta30d,
        allTimeLow,
        tvtHistory,
        tvgSeries,
        zoneRatio,
        zoneAction,
        pegOk: protocolHealth?.pegOk ?? null,
        openCriticalAlerts: protocolHealth?.alerts?.criticalOpen ?? null,
        openWarningAlerts: protocolHealth?.alerts?.warningOpen ?? null,
        dormantCount: protocolHealth?.deposits?.dormant ?? 0,
        abandonedCount: protocolHealth?.deposits?.abandoned ?? 0,
        deposits24h,
        deposits7d,
        deposits30d,
        exits30d,
        totalYieldPaid30d,
        openLoanCount: protocolHealth?.loans?.openCount ?? 0,
        atRiskLoanCount: protocolHealth?.loans?.atRiskCount ?? 0,
        lendingTvl: lendingHealth?.tvl ? BigInt(lendingHealth.tvl) : null
    };
}

function bigOr(...values) {
    for (const v of values) {
        if (v == null) continue;
        if (typeof v === "bigint") return v;
        try {
            return BigInt(v);
        } catch {
            /* fall through */
        }
    }
    return 0n;
}

function buildSparkline(series) {
    if (!series || series.length < 2) return null;
    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = max - min || 1;
    const w = 120;
    const h = 28;
    const step = w / (series.length - 1);
    const pts = series.map((v, i) => {
        const x = i * step;
        const y = h - ((v - min) / range) * (h - 4) - 2;
        return [x, y];
    });
    const line = "M" + pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L");
    const area = `${line} L${w} ${h} L0 ${h} Z`;
    return { line, area };
}

function countRatchetEvents(history) {
    if (!history || history.length < 2) return 0;
    let count = 0;
    let max = history[0].tvt;
    for (let i = 1; i < history.length; i++) {
        if (history[i].tvt > max) {
            count += 1;
            max = history[i].tvt;
        }
    }
    return count;
}

function ratchetDelta(history, days) {
    if (!history || history.length < 2) return null;
    // Chain time: cutoff is matched against chain snapshot timestamps.
    const cutoff = chainNowMs() - days * 24 * 60 * 60 * 1000;
    const past = history.find((p) => p.timestamp >= cutoff);
    const latest = history[history.length - 1];
    if (!past || !latest || past.tvt === 0n) return null;
    const delta = Number(latest.tvt - past.tvt) / Number(past.tvt);
    return delta;
}

function formatUsdc(amount, { decimals = 0 } = {}) {
    if (amount == null) return "—";
    const n = Number(amount) / 1_000_000;
    if (!Number.isFinite(n)) return "—";
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

function formatNumber(amount, opts) {
    if (amount == null) return "—";
    const n = Number(amount);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString("en-US", opts);
}

// TVG floor per token in plain USDC, derived at full float precision from the
// raw snapshot fields. The on-chain `treasuryValuePerToken` is an integer that
// rounds to 0 while the treasury is tiny next to ~50B circulating GIL, so the
// ratchet chart can't rely on it. treasury USDC (6dp) ÷ circulating GIL (18dp)
// gives the real backing per token — e.g. $207.5 ÷ 49.79B ≈ $0.000000004/GIL.
function intrinsicUsdcPerGil(p) {
    // Field names differ by source: /api/tvt/current → totalAllocated /
    // circulatingSupply; useTvt's mapped history items → allocated / supply.
    // Accept both so the same helper works for the live figure AND the chart.
    const alloc = Number(p?.totalAllocated ?? p?.allocated ?? 0) / 1e6; // treasury USDC
    const circ = Number(p?.circulatingSupply ?? p?.supply ?? 0) / 1e18; // circulating GIL
    if (!(circ > 0)) return 0;
    return alloc / circ; // USDC per GIL
}

// Plain-decimal USDC formatter (never scientific notation) for the tiny
// early-stage floor figures the ratchet chart surfaces.
function formatUsdcSmall(n) {
    if (!Number.isFinite(n) || n <= 0) return "—";
    if (n < 0.0001) {
        const decimals = Math.min(20, Math.max(6, 2 - Math.floor(Math.log10(n))));
        return `$${n.toFixed(decimals)}`;
    }
    return `$${n.toFixed(6)}`;
}

function formatTvg(tvtRaw) {
    if (tvtRaw == null) return "—";
    // tvt scaled 1e18 (USDC has 6dp; circulating supply is GIL 1e18).
    // Per-GIL backing in USDC is tvt / 1e18 with USDC being 6dp.
    // Treat tvt as USDC-per-GIL × 1e18 → divide by 1e30 to get plain USDC.
    const n = Number(tvtRaw) / 1e30;
    if (!Number.isFinite(n) || n <= 0) return "—";
    if (n < 0.0001) {
        // Plain decimal with ~3 significant figures — never scientific
        // notation ("e-7"), which users can't read.
        const decimals = Math.min(20, Math.max(6, 2 - Math.floor(Math.log10(n))));
        return `$${n.toFixed(decimals)} / $GILD`;
    }
    return `$${n.toFixed(6)} / $GILD`;
}

// Live GIL market price. `spotPrice` is USDC-per-GIL × 1e8 (TreasuryExecutor
// scale), so divide by 1e8. Same small-value handling as formatTvg.
function formatGilPrice(spotRaw) {
    if (spotRaw == null) return "—";
    const n = Number(spotRaw) / 1e8;
    if (!Number.isFinite(n) || n <= 0) return "—";
    if (n < 0.0001) {
        // Plain decimal with ~3 significant figures — never scientific
        // notation ("e-7"), which users can't read.
        const decimals = Math.min(20, Math.max(6, 2 - Math.floor(Math.log10(n))));
        return `$${n.toFixed(decimals)} / $GILD`;
    }
    return `$${n.toFixed(6)} / $GILD`;
}

function formatPercent(value, { decimals = 1 } = {}) {
    if (value == null) return "—";
    const n = Number(value) * 100;
    if (!Number.isFinite(n)) return "—";
    return `${n >= 0 ? "+" : ""}${n.toFixed(decimals)}%`;
}

function formatBps(bps) {
    if (bps == null) return "—";
    return `${(Number(bps) / 100).toFixed(1)}%`;
}

function shortAddress(addr) {
    if (!addr) return "—";
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/* -------------------------------------------------------------------------- */
/*  Hero                                                                       */
/* -------------------------------------------------------------------------- */

function ProtocolHealthHero({ aggregate }) {
    return (
        <section className="relative overflow-hidden bg-[#f6f6ec] px-5 pt-[56px] pb-[44px] md:px-8 md:pt-[80px] md:pb-[64px]">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse 600px 200px at 50% 0%, rgba(201,162,59,0.07), transparent), radial-gradient(ellipse 800px 300px at 50% 100%, rgba(12,38,24,0.04), transparent)"
                }}
            />
            <div className="relative mx-auto max-w-[880px] text-center">
                <p className="mb-5 font-manrope text-[12px] font-semibold uppercase tracking-[0.22em] text-[#b98c23]">
                    Protocol Health
                </p>
                <h1 className="mb-6 font-serif text-[40px] font-normal leading-[1.04] tracking-[-0.02em] text-[#b98c23] sm:text-[56px] md:text-[64px] lg:text-[72px]">
                    Open numbers.{" "}
                    <em className="font-light italic text-[#102712]/65">
                        Verifiable trust.
                    </em>
                </h1>
                <p className="mx-auto max-w-[620px] font-manrope text-[15px] leading-[1.55] text-[#102712] sm:text-[17px]">
                    Every metric on this page is sourced directly from on-chain
                    activity. Headline numbers. More details lower down. Link to
                    the contracts at the bottom.
                </p>

                <div className="mt-7 inline-flex flex-wrap items-center justify-center gap-2.5">
                    <span className="inline-flex items-center gap-2.5 rounded-full border border-[#102712]/10 bg-[#102712]/[0.05] px-4 py-2 font-mono text-[12px] text-[#102712]">
                        <PulseDot variant={aggregate.pegOk === false ? "warn" : "ok"} />
                        {aggregate.pegOk === false ? "Live · USDC peg alert active" : "Live · indexed on-chain"}
                    </span>
                    <ZoneIndicator />
                    {/* §M6 — moved up from the old mid-page "View Full
                        Protocol Health" bar, which the client found
                        redundant where it sat. */}
                    <a
                        href="#full-health"
                        className="inline-flex items-center gap-2 rounded-full bg-[#213e28] px-4 py-2 font-manrope text-[12px] font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#0e2c19]"
                    >
                        View full protocol health
                        <svg
                            className="h-3 w-3"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    </a>
                </div>
            </div>
        </section>
    );
}

function PulseDot({ variant = "ok" }) {
    const palette = variant === "warn" ? "bg-[#d9a73a]" : "bg-[#6fbf7c]";
    return (
        <span className="relative inline-flex h-2 w-2 shrink-0 items-center justify-center">
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${palette} opacity-60`} />
            <span className={`relative inline-flex h-2 w-2 rounded-full ${palette}`} />
        </span>
    );
}

/* -------------------------------------------------------------------------- */
/*  Trust Layer — 5 headline metric cards                                      */
/* -------------------------------------------------------------------------- */

function TrustLayer({ aggregate }) {
    const metrics = [
        {
            label: "Forward Interest Coverage",
            value: aggregate.coverageDays != null ? aggregate.coverageDays.toLocaleString("en-US") : "—",
            unit: "Days",
            // §M6 — spell out the formula on the card itself so the number
            // is self-explaining: (Yield Reserve + rest of Treasury) ÷ the
            // daily coupon bill on all active principal.
            note: "Yield Reserve + Treasury ÷ the daily coupon bill on all active principal.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        },
        {
            label: "Total Value Locked",
            value: formatUsdc(aggregate.totalActivePrincipal),
            unit: "TVL",
            note: "Aggregate principal across all Term Deposits.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        },
        {
            label: "Total Yield Paid (30d)",
            value: aggregate.totalYieldPaid30d > 0
                ? `$${aggregate.totalYieldPaid30d.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
                : "—",
            unit: "USDC",
            note: "Cumulative interest distributed in the last 30 days.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        },
        {
            label: "Active Term Deposits",
            value: aggregate.activeDepositCount != null ? aggregate.activeDepositCount.toLocaleString("en-US") : "—",
            unit: "Positions",
            note: "Standard plus Turbo, currently in-term.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        },
        {
            label: "Safe Vault Balance",
            value: formatUsdc(aggregate.safeVaultBalance),
            // §M6 (Jul 2026 client review): "This is probably not true. 80%
            // of TVL won't be in the Safe because of Leverage & Turbo."
            // Correct — 80% is only the ROUTING share at deposit time; every
            // loan draw (user Leverage and the Turbo loop) pulls USDC back
            // out of the vault. So the unit is now the MEASURED share, and
            // the note names the advanced amount instead of claiming the
            // vault is untouched.
            unit: "Segregated claim",
            note: aggregate.outstandingLoanPrincipal > 0n
                ? `80% of every deposit is routed here. ${formatUsdc(aggregate.outstandingLoanPrincipal)} is currently advanced back out as Leverage / Turbo loans, leaving ${formatUsdc(aggregate.safeVaultCash)} (${aggregate.safeVaultShareOfTvlPct}% of TVL) held as USDC.`
                : "80% of every deposit is routed here. Nothing is currently advanced out.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        }
    ];

    return (
        <section className="relative bg-[#213e28] px-5 py-[56px] md:px-8 md:py-[88px]">
            <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{
                    background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)"
                }}
            />
            <div className="mx-auto max-w-[1280px]">
                <div className="mb-12 text-center">
                    <p className="mb-4 font-manrope text-[12px] font-semibold uppercase tracking-[0.22em] text-[#c4b050]">
                        The Trust Layer
                    </p>
                    <h2 className="font-serif text-[30px] font-normal leading-[1.1] tracking-[-0.015em] text-[#ffffff] sm:text-[38px] md:text-[46px]">
                        Five numbers that tell the whole story.
                    </h2>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {metrics.map((m, idx) => (
                        <MetricSparkCard key={m.label} metric={m} gradientId={`spark-${idx}`} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function MetricSparkCard({ metric, gradientId }) {
    return (
        <div className="group flex flex-col rounded-[14px] border border-[#ffffff]/[0.12] bg-[#162e1f] px-[22px] pt-6 pb-[22px] transition-all hover:-translate-y-0.5 hover:border-[#ffffff]/[0.22]">
            <div className="mb-4 font-manrope text-[10.5px] font-bold uppercase leading-[1.3] tracking-[0.14em] text-[#c4b050]">
                {metric.label}
            </div>
            <div className="font-serif text-[34px] font-normal leading-none tracking-[-0.02em] text-[#ffffff] sm:text-[38px]">
                {metric.value}
            </div>
            <div className="mt-1 mb-3.5 font-manrope text-[12px] font-medium uppercase tracking-[0.02em] text-[rgba(255,255,255,0.7)]">
                {metric.unit}
            </div>
            <svg
                className="mt-2 mb-2.5 h-7 w-full"
                viewBox="0 0 120 28"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0" stopColor="#c4b050" stopOpacity="0.35" />
                        <stop offset="1" stopColor="#c4b050" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {metric.sparkArea ? <path d={metric.sparkArea} fill={`url(#${gradientId})`} /> : null}
                {metric.sparkLine ? <path d={metric.sparkLine} stroke="#c4b050" strokeWidth="1.5" fill="none" /> : null}
            </svg>
            <div className="mt-auto border-t border-[#ffffff]/[0.12] pt-3 font-manrope text-[11.5px] leading-[1.4] text-[rgba(255,255,255,0.7)]">
                {metric.note}
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Full Protocol Health — 3-col grid                                          */
/* -------------------------------------------------------------------------- */

function FullProtocolHealth({ aggregate }) {
    return (
        <section
            id="full-health"
            className="bg-[#f6f6ec] px-5 py-[56px] md:px-8 md:py-[80px] lg:py-[96px]"
        >
            <div className="mx-auto max-w-[1280px]">
                <div className="mb-12 text-center">
                    <p className="mb-4 font-manrope text-[12px] font-semibold uppercase tracking-[0.22em] text-[#b98c23]">
                        Full Protocol Health
                    </p>
                    <h2 className="font-serif text-[30px] font-normal leading-[1.1] tracking-[-0.015em] text-[#b98c23] sm:text-[38px] md:text-[46px]">
                        Every counter, every category.
                    </h2>
                </div>

                <div className="grid grid-cols-1 gap-[22px] md:grid-cols-2 lg:grid-cols-3">
                    <YieldPerformanceCard aggregate={aggregate} />
                    <TreasuryHealthCard aggregate={aggregate} />
                    <RiskSafetyCard aggregate={aggregate} />
                    <RatchetChartCard aggregate={aggregate} />
                    <CapitalAllocationCard />
                    <LiquidityFeesCard aggregate={aggregate} />
                    <UserActivityCard aggregate={aggregate} />
                </div>
            </div>
        </section>
    );
}

/* ---------- card primitives ---------- */

function HealthCard({ title, subtitle, children, dark = false, className = "" }) {
    const surface = dark
        ? "bg-[#162e1f] border-[#ffffff]/[0.22] text-[#ffffff]"
        : "bg-[#f4f4ec] border-[#102712]/[0.18]";
    const titleColor = dark ? "text-[#ffffff]" : "text-[#102712]";
    const subColor = dark ? "text-[rgba(255,255,255,0.7)]" : "text-[#102712]";
    return (
        <article
            className={`flex flex-col rounded-[14px] border px-[26px] py-7 ${surface} ${className}`}
        >
            <div className="mb-5">
                <div
                    className={`mb-1.5 font-serif text-[22px] font-medium tracking-[-0.01em] ${titleColor}`}
                >
                    {title}
                </div>
                <div
                    className={`font-serif text-[13.5px] font-light italic ${subColor}`}
                >
                    {subtitle}
                </div>
            </div>
            {children}
        </article>
    );
}

function MetricRow({ label, value, trend, statusDot, dark = false }) {
    const borderColor = dark ? "border-[#ffffff]/[0.12]" : "border-[#102712]/[0.08]";
    const labelColor = dark ? "text-[#ffffff]" : "text-[#102712]";
    const valueColor = dark ? "text-[#ffffff]" : "text-[#102712]";
    return (
        <div
            className={`flex items-center justify-between border-b py-[13px] text-[14px] last:border-b-0 ${borderColor}`}
        >
            <span className={`inline-flex items-center gap-2 ${labelColor}`}>
                {statusDot ? <StatusDot variant={statusDot} /> : null}
                {label}
            </span>
            <span
                className={`inline-flex items-center font-mono text-[13.5px] font-medium tracking-[-0.01em] ${valueColor}`}
            >
                {value}
                {trend ? <Trend variant={trend.variant}>{trend.label}</Trend> : null}
            </span>
        </div>
    );
}

function Trend({ variant = "flat", children }) {
    const palette =
        variant === "up"
            ? "bg-[#6fbf7c]/[0.15] text-[#2e7a3a]"
            : variant === "down"
            ? "bg-[#c93030]/[0.15] text-[#c93030]"
            : "bg-[#102712]/[0.06] text-[#102712]";
    return (
        <span
            className={`ml-2 inline-flex items-center gap-1 rounded px-[7px] py-0.5 font-mono text-[11px] ${palette}`}
        >
            {children}
        </span>
    );
}

function StatusDot({ variant = "ok" }) {
    const palette =
        variant === "warn"
            ? "bg-[#d9a73a] ring-[#d9a73a]/30"
            : variant === "danger"
            ? "bg-[#c93030] ring-[#c93030]/30"
            : "bg-[#6fbf7c] ring-[#6fbf7c]/30";
    return (
        <span
            aria-hidden="true"
            className={`inline-block h-2 w-2 rounded-full ring-[3px] ${palette}`}
        />
    );
}

/* ---------- individual cards ---------- */

function YieldPerformanceCard({ aggregate }) {
    // 20% APR is the contractual rate (config.protocol.annualRateBps); the
    // "paid" figures should match unless the contract is in violation.
    const apr = (config.protocol.annualRateBps || 2000) / 100;
    const aprStr = `${apr.toFixed(2)}%`;
    const dailyInterest =
        (aggregate.totalActivePrincipal * BigInt(config.protocol.annualRateBps || 2000)) /
        (10_000n * 365n);
    return (
        <HealthCard
            title="Yield Performance"
            subtitle="20% APR paid from protocol reserves, not emissions."
        >
            <MetricRow label="Contractual APR" value={aprStr} />
            <MetricRow
                label="Daily interest obligation"
                value={formatUsdc(dailyInterest, { decimals: 2 })}
            />
            <MetricRow
                label="Yield paid · last 30d"
                value={
                    aggregate.totalYieldPaid30d > 0
                        ? `$${aggregate.totalYieldPaid30d.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
                        : "—"
                }
            />
            <MetricRow
                label="Active deposits"
                value={
                    aggregate.activeDepositCount != null
                        ? aggregate.activeDepositCount.toLocaleString("en-US")
                        : "—"
                }
            />
        </HealthCard>
    );
}

function TreasuryHealthCard({ aggregate }) {
    const deltaVariant =
        aggregate.ratchetDelta30d == null
            ? "flat"
            : aggregate.ratchetDelta30d > 0
            ? "up"
            : aggregate.ratchetDelta30d < 0
            ? "down"
            : "flat";
    return (
        <HealthCard
            title="Market Treasury Health"
            subtitle="Shows intrinsic backing and ratchet progression."
        >
            <MetricRow
                label="Treasury balance (total allocated)"
                value={formatUsdc(aggregate.treasuryAllocated)}
            />
            {/* What the treasury is DOING right now — Skimming vs Harvesting was
                impossible to tell from the "Premium" pill alone. */}
            <MetricRow
                statusDot={
                    aggregate.zoneAction == null
                        ? undefined
                        : aggregate.zoneAction.label === "Buying"
                        ? "ok"
                        : aggregate.zoneAction.label === "Holding"
                        ? undefined
                        : "warn"
                }
                label="Treasury action"
                value={
                    aggregate.zoneAction
                        ? `${aggregate.zoneAction.label} · ${aggregate.zoneRatio.toFixed(2)}× TVG`
                        : "—"
                }
            />
            {aggregate.zoneAction ? (
                <div className="-mt-1 pb-2 text-[11px] leading-snug text-[#102712]/55">
                    {aggregate.zoneAction.desc}
                </div>
            ) : null}
            <MetricRow
                label="TVG current floor"
                value={
                    aggregate.tvtCurrentFloorUsdc > 0
                        ? `${formatUsdcSmall(aggregate.tvtCurrentFloorUsdc)} / $GILD`
                        : "—"
                }
            />
            <MetricRow
                label="$GILD market price"
                value={formatGilPrice(aggregate.gilSpotPrice)}
            />
            {/* §M6 (Jul 2026 client review): "What is the TVG 30 day delta?
                And what are the Indexed Ratchet events?" — both rows were bare
                numbers with no definition. Each now carries a one-line
                explainer underneath. */}
            <MetricRow
                label="TVG 30-day delta"
                value={formatPercent(aggregate.ratchetDelta30d)}
                trend={{
                    variant: deltaVariant,
                    label: deltaVariant === "up" ? "↑" : deltaVariant === "down" ? "↓" : "="
                }}
            />
            <div className="-mt-1 pb-2 text-[11px] leading-snug text-[#102712]/55">
                How much the treasury backing per $GILD has moved over the last
                30 days. Token price is not part of it.
            </div>
            <MetricRow label="Indexed ratchet events" value={String(aggregate.ratchetEvents || 0)} />
            <div className="-mt-1 text-[11px] leading-snug text-[#102712]/55">
                Number of times in the indexed history that TVG set a new
                all-time high — each one permanently raises the floor, because
                the floor never moves back down.
            </div>
        </HealthCard>
    );
}

function RiskSafetyCard({ aggregate }) {
    return (
        <HealthCard
            title="Risk & Safety"
            subtitle={`Answers the "what if it breaks" question.`}
        >
            <MetricRow
                statusDot={aggregate.coverageDays == null ? undefined : aggregate.coverageDays >= 365 ? "ok" : aggregate.coverageDays >= 30 ? "warn" : "danger"}
                label="Forward coverage (Yield Reserve + Treasury)"
                value={aggregate.coverageDays != null ? `${aggregate.coverageDays.toLocaleString("en-US")} days` : "—"}
            />
            {/* §M6 — the headline card now uses the whole Distribution
                Reserve; keep the stricter Yield-Reserve-only runway visible
                here so both figures are auditable side by side. */}
            <MetricRow
                statusDot={
                    aggregate.coverageDaysYieldReserveOnly == null
                        ? undefined
                        : aggregate.coverageDaysYieldReserveOnly >= 365
                        ? "ok"
                        : aggregate.coverageDaysYieldReserveOnly >= 30
                        ? "warn"
                        : "danger"
                }
                label="…of which Yield Reserve alone"
                value={
                    aggregate.coverageDaysYieldReserveOnly != null
                        ? `${aggregate.coverageDaysYieldReserveOnly.toLocaleString("en-US")} days`
                        : "—"
                }
            />
            <MetricRow
                statusDot={aggregate.coverageRatioBps == null ? undefined : aggregate.coverageRatioBps >= 10_000 ? "ok" : "warn"}
                label="Coverage ratio"
                value={formatBps(aggregate.coverageRatioBps)}
            />
            <MetricRow
                statusDot={aggregate.openCriticalAlerts === 0 ? "ok" : "danger"}
                label="Critical alerts (open)"
                value={String(aggregate.openCriticalAlerts ?? "—")}
            />
            <MetricRow
                statusDot={aggregate.pegOk === false ? "danger" : "ok"}
                label="USDC peg status"
                value={aggregate.pegOk === false ? "De-pegged" : aggregate.pegOk === true ? "Healthy" : "—"}
            />
        </HealthCard>
    );
}

function RatchetChartCard({ aggregate }) {
    // Prefer the 30-day windowed series (multi-day coverage → visible movement);
    // fall back to the raw last-90 snapshots if the series fetch hasn't landed.
    const series = aggregate.tvgSeries;
    const history = (series && series.length >= 2 ? series : aggregate.tvtHistory) || [];
    // Plot the ACTUAL derived intrinsic (treasury ÷ supply) over time — same
    // figure the My Tokens / Dashboard TVG charts show. The running-max "floor"
    // version flat-lined during stable periods and didn't read as a chart; the
    // raw intrinsic tracks the real backing so the curve has genuine movement.
    const tvts = history.map((p) => intrinsicUsdcPerGil(p));
    // §M6 (Jul 2026 client review) — spot-price overlay. `marketPrice` is
    // USDC-per-GIL × 1e8, the SAME unit as the derived intrinsic once
    // scaled, so both lines share one y-axis and the gap between them reads
    // directly as premium/discount to TVG. Samples where the monitor
    // couldn't read spotPrice() come back as 0 and are skipped (null) so
    // the line breaks instead of collapsing to the axis.
    const spots = history.map((p) => {
        const raw = Number(p?.marketPrice ?? 0);
        return raw > 0 ? raw / 1e8 : null;
    });
    const hasSpot = spots.filter((v) => v != null).length >= 2;
    const hasData = tvts.length >= 2 && tvts.some((v) => v > 0);
    const positive = tvts.filter((v) => v > 0);
    const spotPositive = spots.filter((v) => v != null && v > 0);
    const w = 720;
    const h = 200;
    const pad = 20;
    const step = hasData ? (w - pad * 2) / (history.length - 1) : 0;

    // §M6 — how far apart the two series are. Drives the layout decision below.
    const tvgMid = positive.length ? positive[positive.length - 1] : 0;
    const spotMid = spotPositive.length ? spotPositive[spotPositive.length - 1] : 0;
    const premiumX = tvgMid > 0 && spotMid > 0 ? spotMid / tvgMid : null;

    // §M6 (Jul 2026 client review) — "TVG Ratchet is good, but is it possible
    // to overlay token price for context? Or have it as a separate chart, but
    // some scale."
    //
    // We tried the overlay on one axis. It cannot work at these magnitudes:
    // spot currently trades ~53x TVG, so on a shared axis TVG's own movement
    // (a ~1.7% ratchet) renders in 0.03 of a pixel — the card called "TVG
    // Ratchet History" stops showing the ratchet entirely. A log axis only
    // takes that to 0.6px, still invisible.
    //
    // So we take the client's own second option once the gap is wide: two
    // bands stacked in the same frame, sharing the x-axis, each auto-scaled to
    // its OWN series. Both shapes stay readable, nothing is squashed, and the
    // relationship is stated as a number ("spot is 53x TVG") rather than
    // implied by a gap the eye can't measure. Below 4x the single shared axis
    // is genuinely better, so we keep it.
    const split = hasData && hasSpot && premiumX != null && premiumX > 4;

    // Band geometry: one full-height plot, or two half-height bands.
    const BAND_GAP = 14;
    const bandH = split ? (h - pad * 2 - BAND_GAP) / 2 : h - pad * 2;
    const tvgTop = pad;
    const spotTop = split ? pad + bandH + BAND_GAP : pad;

    /** Maps a value to a y pixel inside a band auto-scaled to [lo, hi]. */
    function bandY(v, lo, hi, top) {
        if (!(hi > lo)) return top + bandH / 2; // flat series — pin mid-band
        const t = (v - lo) / (hi - lo);
        return top + bandH - t * bandH;
    }

    // TVG scale: its own range when split, otherwise shared with spot.
    const tvgLo = positive.length ? Math.min(...positive) : 0;
    const tvgHi = positive.length ? Math.max(...positive) : 0;
    const spotLo = spotPositive.length ? Math.min(...spotPositive) : 0;
    const spotHi = spotPositive.length ? Math.max(...spotPositive) : 0;
    const sharedLo = Math.min(tvgLo, hasSpot ? spotLo : tvgLo);
    const sharedHi = Math.max(tvgHi, hasSpot ? spotHi : tvgHi);

    const yTvg = (v) =>
        split ? bandY(v, tvgLo, tvgHi, tvgTop) : bandY(v, sharedLo, sharedHi, tvgTop);
    const ySpot = (v) =>
        split ? bandY(v, spotLo, spotHi, spotTop) : bandY(v, sharedLo, sharedHi, tvgTop);

    const linePts = hasData ? tvts.map((v, i) => [pad + i * step, yTvg(v)]) : [];
    const linePath = hasData
        ? "M" + linePts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L")
        : "";
    // Area fills down to the bottom of the TVG band, not the frame.
    const tvgBandBottom = tvgTop + bandH;
    const areaPath = hasData
        ? `${linePath} L${(w - pad).toFixed(1)} ${tvgBandBottom} L${pad} ${tvgBandBottom} Z`
        : "";
    // Spot line is drawn as one or more segments so a gap in the samples
    // (monitor couldn't read the pool) leaves a break rather than a
    // straight line through zero.
    const spotSegments = [];
    if (hasData && hasSpot) {
        let run = [];
        spots.forEach((v, i) => {
            if (v == null) {
                if (run.length >= 2) spotSegments.push(run);
                run = [];
                return;
            }
            run.push([pad + i * step, ySpot(v)]);
        });
        if (run.length >= 2) spotSegments.push(run);
    }
    const spotPaths = spotSegments.map(
        (seg) => "M" + seg.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L")
    );
    const last = linePts.length ? linePts[linePts.length - 1] : null;
    // Stats below the chart describe the TVG series ONLY. `min`/`max` now
    // span both series (they set the shared y-scale), so the all-time-low
    // and total-rise figures read from the TVG samples directly — otherwise
    // a low spot print would masquerade as a TVG low.
    const tvgMin = positive.length ? Math.min(...positive) : 0;
    const allTimeLowUsdc = hasData ? tvgMin : null;
    const currentFloorUsdc = hasData ? tvts[tvts.length - 1] : null;
    // How far the current TVG sits above its lowest point in the window —
    // stays non-negative and reads naturally as "risen X% above the low".
    const totalRise =
        hasData && tvgMin > 0 ? (currentFloorUsdc - tvgMin) / tvgMin : null;

    return (
        <HealthCard
            dark
            title="TVG Ratchet History"
            subtitle="Treasury-backed intrinsic value (TVG) per token, tracked over time."
            className="lg:col-span-2"
        >
            <svg
                className="my-3 w-full"
                viewBox={`0 0 ${w} 220`}
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="ratchetFill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0" stopColor="#c4b050" stopOpacity="0.35" />
                        <stop offset="1" stopColor="#c4b050" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {/* Grid: one set per band when split, so a line never reads
                    as a shared level between two independent scales. */}
                {(split
                    ? [tvgTop, tvgTop + bandH, spotTop, spotTop + bandH]
                    : [40, 100, 160]
                ).map((y, i) => (
                    <line
                        key={i}
                        x1="0"
                        y1={y}
                        x2={w}
                        y2={y}
                        stroke="rgba(255,255,255,0.08)"
                        strokeWidth="1"
                    />
                ))}
                {/* Band labels — without these two independently-scaled plots
                    in one frame would be ambiguous. */}
                {split ? (
                    <>
                        <text x={pad} y={tvgTop - 6} fontSize="9" fill="#FFC400" opacity="0.8">
                            Intrinsic value (TVG)
                        </text>
                        <text x={pad} y={spotTop - 6} fontSize="9" fill="#22C55E" opacity="0.8">
                            $GILD market price
                        </text>
                    </>
                ) : null}

                {hasData ? (
                    <>
                        <path d={areaPath} fill="url(#ratchetFill)" />
                        {/* Spot underneath the TVG line so the ratchet stays
                            the hero of the chart. */}
                        {spotPaths.map((d, i) => (
                            <path
                                key={i}
                                d={d}
                                stroke="#22C55E"
                                strokeWidth="1.6"
                                strokeDasharray="4 3"
                                fill="none"
                            />
                        ))}
                        <path d={linePath} stroke="#FFC400" strokeWidth="2" fill="none" />
                        {last ? (
                            <>
                                <circle cx={last[0]} cy={last[1]} r="5" fill="#FFC400" />
                                <circle
                                    cx={last[0]}
                                    cy={last[1]}
                                    r="9"
                                    fill="none"
                                    stroke="#FFC400"
                                    strokeOpacity="0.35"
                                    strokeWidth="1.5"
                                />
                            </>
                        ) : null}
                    </>
                ) : (
                    <text
                        x={w / 2}
                        y={h / 2}
                        textAnchor="middle"
                        fontFamily="ui-monospace, monospace"
                        fontSize="12"
                        fill="rgba(255,255,255,0.6)"
                    >
                        Awaiting first TVG snapshot
                    </text>
                )}
            </svg>
            {/* Legend — only advertises the spot line when there is actually
                spot data in the window. */}
            <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[10.5px] text-[#ffffff]/70">
                <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-[2px] w-4 rounded-full bg-[#FFC400]" />
                    Intrinsic value (TVG)
                </span>
                {hasSpot ? (
                    <span className="inline-flex items-center gap-1.5">
                        <span
                            className="inline-block h-[2px] w-4 rounded-full"
                            style={{ backgroundImage: "repeating-linear-gradient(90deg,#22C55E 0 4px,transparent 4px 7px)" }}
                        />
                        $GILD market price
                    </span>
                ) : (
                    <span className="text-[#ffffff]/45">
                        Market price appears here once the pool has priced ticks in this window.
                    </span>
                )}
                <span className="text-[#ffffff]/45">
                    {split
                        ? `· separate scales — spot is ${premiumX.toFixed(1)}× TVG, too far apart to share an axis`
                        : "· same axis, both in USDC / $GILD"}
                </span>
            </div>
            <div className="grid grid-cols-1 gap-[18px] border-t border-[#ffffff]/[0.12] pt-[18px] sm:grid-cols-4">
                <RatchetStat
                    k="All-time low"
                    v={allTimeLowUsdc != null ? `${formatUsdcSmall(allTimeLowUsdc)} / $GILD` : "—"}
                />
                <RatchetStat
                    k="Current floor"
                    v={currentFloorUsdc != null ? `${formatUsdcSmall(currentFloorUsdc)} / $GILD` : "—"}
                />
                <RatchetStat
                    k="Total rise"
                    v={totalRise != null ? formatPercent(totalRise) : "—"}
                />
                {/* §M6 — the "context" the client wanted from the overlay,
                    stated as a number. At these magnitudes the eye cannot
                    measure it off the plot, so we say it outright. */}
                <RatchetStat
                    k="Spot vs TVG"
                    v={premiumX != null ? `${premiumX.toFixed(1)}× TVG` : "—"}
                />
            </div>
        </HealthCard>
    );
}

function RatchetStat({ k, v }) {
    // Split the "<number> / $GILD" values so the unit renders small — the tiny
    // TVG figures are long enough that "/ $GILD" wrapped onto its own line at
    // the full size. Keep the whole value on one line (no wrap).
    const idx = typeof v === "string" ? v.indexOf(" / ") : -1;
    const main = idx >= 0 ? v.slice(0, idx) : v;
    const unit = idx >= 0 ? v.slice(idx) : "";
    return (
        <div>
            <div className="mb-1.5 font-manrope text-[11px] font-semibold uppercase tracking-[0.12em] text-[#c4b050]">
                {k}
            </div>
            <div className="flex items-baseline whitespace-nowrap font-serif text-[20px] font-normal tracking-[-0.01em] text-[#ffffff]">
                {main}
                {unit ? <span className="ml-1 text-[12px] text-[#ffffff]/60">{unit.trim()}</span> : null}
            </div>
        </div>
    );
}

function CapitalAllocationCard() {
    return (
        <HealthCard
            title="Capital Allocation"
            subtitle="Where every dollar of every deposit goes."
        >
            <div className="mt-1 flex flex-col items-center gap-7 sm:flex-row">
                <AllocationDonut />
                <div className="flex flex-1 flex-col">
                    <AllocationLegendRow swatch="#213e28" label="Safe Vault" value="80%" />
                    <AllocationLegendRow swatch="#c4b050" label="Token Purchase" value="10%" />
                    <AllocationLegendRow swatch="#487c3f" label="Distribution Reserve" value="9%" />
                    <AllocationLegendRow swatch="#b98c23" label="Liquidity Pool" value="1%" />
                </div>
            </div>
        </HealthCard>
    );
}

function AllocationDonut() {
    return (
        <div className="relative h-[168px] w-[168px] shrink-0">
            <svg width="168" height="168" viewBox="0 0 168 168" aria-hidden="true">
                <circle cx="84" cy="84" r="60" fill="none" stroke="rgba(33,62,40,0.15)" strokeWidth="20" />
                <circle cx="84" cy="84" r="60" fill="none" stroke="#213e28" strokeWidth="20" strokeDasharray="301.59 376.99" transform="rotate(-90 84 84)" />
                <circle cx="84" cy="84" r="60" fill="none" stroke="#c4b050" strokeWidth="20" strokeDasharray="37.7 376.99" strokeDashoffset="-301.59" transform="rotate(-90 84 84)" />
                <circle cx="84" cy="84" r="60" fill="none" stroke="#487c3f" strokeWidth="20" strokeDasharray="33.93 376.99" strokeDashoffset="-339.29" transform="rotate(-90 84 84)" />
                <circle cx="84" cy="84" r="60" fill="none" stroke="#b98c23" strokeWidth="20" strokeDasharray="3.77 376.99" strokeDashoffset="-373.22" transform="rotate(-90 84 84)" />
            </svg>
            <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
                <div>
                    <div className="font-serif text-[32px] leading-none text-[#102712]">100%</div>
                    <div className="mt-1 font-manrope text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#102712]">
                        Allocated
                    </div>
                </div>
            </div>
        </div>
    );
}

function AllocationLegendRow({ swatch, label, value }) {
    return (
        <div className="flex items-center justify-between border-b border-[#102712]/[0.08] py-2.5 last:border-b-0">
            <span className="inline-flex items-center gap-2.5 text-[13.5px] text-[#102712]">
                <span
                    aria-hidden="true"
                    className="inline-block h-[11px] w-[11px] rounded-[3px]"
                    style={{ backgroundColor: swatch }}
                />
                {label}
            </span>
            <span className="font-mono text-[13.5px] font-semibold text-[#102712]">{value}</span>
        </div>
    );
}

function LiquidityFeesCard({ aggregate }) {
    return (
        <HealthCard
            title="Reserve Buckets"
            subtitle="Tier 1 interest coverage and Tier 2 TVG defence."
        >
            <MetricRow
                label="Tier 1 — Interest Reserve"
                value={formatUsdc(aggregate.interestReserve)}
            />
            <MetricRow
                label="Tier 2 — Buyback Reserve"
                value={formatUsdc(aggregate.buybackReserve)}
            />
            <MetricRow
                label="Operational Reserve"
                value={formatUsdc(aggregate.operationalReserve)}
            />
            <MetricRow
                statusDot={aggregate.marketingLocked ? "warn" : "ok"}
                label="Marketing extraction"
                value={aggregate.marketingLocked ? "Locked" : "Open"}
            />
        </HealthCard>
    );
}

function UserActivityCard({ aggregate }) {
    return (
        <HealthCard
            title="User Activity"
            subtitle="Real usage, not promised."
        >
            <MetricRow
                label="Total wallets"
                value={aggregate.totalWallets != null ? formatNumber(aggregate.totalWallets) : "—"}
            />
            <MetricRow
                label="Total Term Deposits"
                value={aggregate.totalDepositCount != null ? formatNumber(aggregate.totalDepositCount) : "—"}
            />
            <MetricRow
                label="New deposits · last 24h"
                value={formatNumber(aggregate.deposits24h)}
            />
            <MetricRow
                label="New deposits · last 7d"
                value={formatNumber(aggregate.deposits7d)}
            />
            <MetricRow
                label="New deposits · last 30d"
                value={formatNumber(aggregate.deposits30d)}
            />
            <MetricRow
                label="Early exits · last 30d"
                value={formatNumber(aggregate.exits30d)}
            />
        </HealthCard>
    );
}

/* -------------------------------------------------------------------------- */
/*  On-chain CTA                                                               */
/* -------------------------------------------------------------------------- */

function OnChainCTA() {
    const explorer = config.network.blockExplorerUrl;
    const contracts = [
        { name: "Safe Vault", addr: config.contracts.safeVault },
        { name: "Distribution Reserve", addr: config.contracts.treasury },
        { name: "Market Treasury", addr: config.contracts.treasuryExecutor },
        { name: "Bond Contract", addr: config.contracts.bond },
        { name: "$GILD Token (ERC-20)", addr: config.contracts.gil }
    ].filter((c) => c.addr);

    return (
        <section className="border-t-2 border-[#213e28] bg-[#f6f6ec] px-5 py-[72px] md:px-8 md:py-[84px]">
            <div className="mx-auto max-w-[920px] text-center">
                <p className="mb-5 font-manrope text-[12px] font-semibold uppercase tracking-[0.22em] text-[#b98c23]">
                    Don&apos;t take our word for it.
                </p>
                <h2 className="mb-4 font-serif text-[28px] font-normal leading-[1.1] tracking-[-0.015em] text-[#b98c23] sm:text-[36px] md:text-[44px]">
                    Read every transaction on-chain.
                </h2>
                <p className="mx-auto mb-9 max-w-[640px] font-manrope text-[16px] text-[#102712]">
                    The Safe Vault, Distribution Reserve, Market Treasury,
                    Bond, and $GILD token contracts are all deployed on
                    {` ${config.network.name}`} and viewable on{" "}
                    {explorer ? new URL(explorer).host : "the block explorer"}.
                </p>

                <div className="mx-auto mb-8 grid max-w-[760px] grid-cols-1 gap-3.5 sm:grid-cols-2">
                    {contracts.length === 0 ? (
                        <p className="col-span-2 font-manrope text-[13px] text-[#102712]/70">
                            Contract addresses are not configured for this build.
                        </p>
                    ) : (
                        contracts.map((c) => (
                            <ContractCard key={c.name} contract={c} explorer={explorer} />
                        ))
                    )}
                </div>

                {explorer ? (
                    <a
                        href={explorer+"/blocks"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2.5 rounded-[10px] bg-[#213e28] px-5 py-3.5 font-manrope text-[14px] font-semibold text-[#ffffff] transition-all hover:-translate-y-0.5 hover:bg-[#0e2c19]"
                    >
                        Open Block Explorer
                        <ArrowOut />
                    </a>
                ) : null}
            </div>
        </section>
    );
}

function ContractCard({ contract, explorer }) {
    const href = explorer ? `${explorer.replace(/\/$/, "")}/address/${contract.addr}` : "#";
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between rounded-[10px] border border-[#102712]/[0.15] bg-[#f4f4ec] px-[22px] py-[18px] text-left no-underline transition-all hover:-translate-y-0.5 hover:border-[#213e28]"
        >
            <div>
                <div className="mb-1 font-manrope text-[14px] font-semibold text-[#102712]">
                    {contract.name}
                </div>
                <div className="font-mono text-[12px] tracking-[-0.02em] text-[#102712]">
                    {shortAddress(contract.addr)}
                </div>
            </div>
            <span className="ml-4 shrink-0 text-[#b98c23]">
                <ArrowOut />
            </span>
        </a>
    );
}

function ArrowOut() {
    return (
        <svg
            className="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M7 17 L17 7 M9 7 L17 7 L17 15" />
        </svg>
    );
}